import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { priceHistory24h, priceHistory30d } from '@/mocks/goldPrices';
import { supabase } from '@/lib/supabase';

type Range = '24h' | '30d';

interface ChartPoint {
  label: string;
  price: number;
}

const ranges: { key: Range; label: string }[] = [
  { key: '24h', label: '24 giờ' },
  { key: '30d', label: '30 ngày' },
];

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}

export default function PriceChart() {
  const [range, setRange] = useState<Range>('30d');
  const [realData, setRealData] = useState<ChartPoint[]>([]);
  const [realLoading, setRealLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (range !== '30d') return;
    let cancelled = false;

    async function load() {
      setRealLoading(true);
      setFetchError(false);
      try {
        const { data: rows, error } = await supabase
          .from('vn_gold_history')
          .select('record_date, buy')
          .eq('type_code', 'SJL1L10')
          .order('record_date', { ascending: true })
          .limit(30);

        if (error) {
          if (!cancelled) {
            setFetchError(true);
            setRealData([]);
          }
          return;
        }

        if (!rows || rows.length < 2) {
          if (!cancelled) {
            setRealData([]);
          }
          return;
        }

        const points = rows.map((r) => ({
          label: formatDateLabel(r.record_date),
          price: Number(r.buy),
        }));

        if (!cancelled) setRealData(points);
      } catch {
        if (!cancelled) {
          setFetchError(true);
          setRealData([]);
        }
      } finally {
        if (!cancelled) setRealLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [range]);

  const hasRealData = realData.length >= 2;
  const isMockData = range === '30d' && !hasRealData && !realLoading;
  const chartData = range === '24h'
    ? priceHistory24h
    : hasRealData
      ? realData
      : priceHistory30d;
  const xKey = range === '24h' ? 'time' : 'label';

  return (
    <div className="rounded-xl border border-background-200/60 p-3 md:p-5 relative">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3 md:mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground-950">Biểu đồ giá vàng SJC</h3>
          <p className="text-xs text-foreground-600">
            Đơn vị: VNĐ / lượng
            {range === '30d' && hasRealData && (
              <span className="text-emerald-600 ml-1">· Dữ liệu thực tế</span>
            )}
            {isMockData && (
              <span className="text-secondary-700 ml-1">· Dữ liệu minh họa</span>
            )}
          </p>
        </div>
        <div className="inline-flex rounded-full border border-background-300/60 p-1 bg-background-100">
          {ranges.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 md:px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                range === r.key ? 'bg-primary-500 text-background-950' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-52 md:h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="oklch(var(--primary-500))" stopOpacity={0.45} />
                <stop offset="95%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300) / 0.5)" vertical={false} />
            <XAxis
              dataKey={xKey}
              tick={{ fill: 'oklch(var(--foreground-600))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'oklch(var(--foreground-600))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `${(v / 1000000).toFixed(1)}tr`}
              width={52}
            />
            <Tooltip
              contentStyle={{
                background: 'oklch(var(--background-100))',
                border: '1px solid oklch(var(--background-300))',
                borderRadius: 8,
                color: 'oklch(var(--foreground-950))',
              }}
              formatter={(value: number | string) => [Number(value).toLocaleString('vi-VN') + 'đ', 'Giá']}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke="oklch(var(--primary-500))"
              strokeWidth={2}
              fill="url(#goldGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}